import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiTerminal } from 'react-icons/fi';

interface LoadingModalProps {
  onLoadComplete: () => void;
}

const loadingLines = [
  'Initializing environment...',
  'Loading modules...',
  'Compiling assets...',
  'Optimizing bundles...',
  'Starting dev server...',
  'Welcome to EVODENUBY',
];

const LoadingModal: React.FC<LoadingModalProps> = ({ onLoadComplete }) => {
  const [progress, setProgress] = useState(0);
  const [currentLine, setCurrentLine] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 1;
      });
    }, 30);

    const lineInterval = setInterval(() => {
      setCurrentLine(prev => {
        if (prev >= loadingLines.length - 1) {
          clearInterval(lineInterval);
          return prev;
        }
        return prev + 1;
      });
    }, 1200);

    const completeTimeout = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => {
        onLoadComplete();
      }, 500);
    }, 8000);

    return () => {
      clearInterval(progressInterval);
      clearInterval(lineInterval);
      clearTimeout(completeTimeout);
    };
  }, [onLoadComplete]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#0d1117]"
        >
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1, ease: 'easeOut' }}
            className="w-full max-w-lg mx-4"
          >
            <div className="rounded-lg border border-[#30363d] bg-[#161b22] overflow-hidden shadow-2xl shadow-black/50">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-[#21262d] border-b border-[#30363d]">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-[#f85149]" />
                  <div className="w-3 h-3 rounded-full bg-[#d29922]" />
                  <div className="w-3 h-3 rounded-full bg-[#3fb950]" />
                </div>
                <span className="ml-auto text-xs text-[#8b949e] font-mono">
                  ~/portfolio
                </span>
              </div>

              <div className="p-6">
                <div className="flex justify-center mb-6">
                  {React.createElement(FiTerminal as React.ComponentType<{ className?: string; size?: number }>, { 
                    className: "text-[#58a6ff]", 
                    size: 32 
                  })}
                </div>

                <div className="mb-6 font-mono text-sm space-y-1">
                  <div className="flex items-center gap-2 text-[#e6edf3]">
                    <span className="text-[#3fb950]">$</span>
                    <span>npx create-portfolio</span>
                  </div>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentLine}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 8 }}
                      transition={{ duration: 0.15 }}
                      className="flex items-center gap-2"
                    >
                      <span className="text-[#8b949e] w-4">
                        {currentLine === loadingLines.length - 1 ? '✓' : '→'}
                      </span>
                      <span className={currentLine === loadingLines.length - 1 ? 'text-[#3fb950]' : 'text-[#e6edf3]'}>
                        {loadingLines[currentLine]}
                        {currentLine < loadingLines.length - 1 && (
                          <span className="inline-block w-2 h-[14px] ml-1 bg-[#58a6ff] animate-pulse align-middle" />
                        )}
                      </span>
                    </motion.div>
                  </AnimatePresence>
                </div>

                <div className="mb-6">
                  <div className="flex justify-between text-xs text-[#8b949e] mb-2 font-mono">
                    <span>Progress</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="h-1.5 bg-[#21262d] rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-[#58a6ff] rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.3, ease: 'easeOut' }}
                    />
                  </div>
                </div>

                <div className="flex flex-wrap justify-center gap-2">
                  {['React', 'TypeScript', 'Node.js', 'Python'].map((tech) => (
                    <span
                      key={tech}
                      className="px-2.5 py-1 text-xs font-mono text-[#8b949e] border border-[#30363d] rounded"
                    >
                      {tech}
                    </span>
                  ))}
                </div>

                <div className="flex justify-center gap-1.5 mt-5">
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      className="w-1.5 h-1.5 rounded-full bg-[#8b949e]"
                      animate={{ opacity: [0.3, 1, 0.3] }}
                      transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingModal;